'use client';

import ImageOptimizer from '@/components/image-optimizer';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from '@/components/ui/carousel';
import Autoplay from 'embla-carousel-autoplay';
import type React from 'react';
import { useEffect, useState } from 'react';

export interface CurriculumStepCarouselItem {
  stepNumber: number;
  title: string;
  [key: string]: unknown; // 다른 필드들을 허용하기 위해 인덱스 시그니처 추가
}

interface CurriculumStepCarouselProps {
  steps: CurriculumStepCarouselItem[];
  renderItem?: (
    step: CurriculumStepCarouselItem,
    index: number,
  ) => React.ReactNode;
}

export default function CurriculumStepCarousel({
  steps,
  renderItem,
}: CurriculumStepCarouselProps) {
  const [api, setApi] = useState<CarouselApi>();
  const [currentIndex, setCurrentIndex] = useState(0);

  // 배러셀 변경 시 현재 인덱스 업데이트
  useEffect(() => {
    if (!api) return;

    const onSelect = () => {
      setCurrentIndex(api.selectedScrollSnap());
    };

    api.on('select', onSelect);
    onSelect(); // 초기값 설정

    return () => {
      api.off('select', onSelect);
    };
  }, [api]);

  // Step 클릭 시 해당 인덱스로 이동
  const handleStepClick = (index: number) => {
    api?.scrollTo(index);
  };

  // 이전/다음 버튼 클릭 핸들러
  const handlePrev = () => {
    api?.scrollPrev();
  };

  const handleNext = () => {
    api?.scrollNext();
  };

  return (
    <div className="desktop:mt-16 mt-10 w-full">
      {/* 상단 네비게이션 바 */}
      <div className="desktop:px-0 desktop:py-6 px-4">
        <div className="desktop:web-w desktop:mx-auto desktop:gap-[20px] desktop:h-[124px] desktop:flex-row desktop:items-center desktop:justify-center desktop:p-0 flex flex-col items-start justify-center gap-4 rounded-xl border border-[#54575D] bg-[#363940] px-7 py-6">
          {steps.map((step, index) => {
            const isActive = currentIndex === index;
            return (
              <button
                key={step.stepNumber}
                onClick={() => handleStepClick(index)}
                className={`desktop:gap-4 desktop:min-w-[208px] desktop:flex-col box-border flex h-full cursor-pointer flex-row items-center justify-center gap-4 transition ${isActive ? 'desktop:border-b-[4px] border-white' : ''}`}
              >
                <span
                  className={`desktop:typo-18 typo-13 font-black transition ${isActive ? 'text-main-orange' : 'text-[#684030]'}`}
                >
                  STEP {step.stepNumber}
                </span>
                <span
                  className={`desktop:typo-28 typo-18 transition ${
                    isActive
                      ? 'font-extrabold text-white'
                      : 'font-bold text-[#A7A8AB]'
                  }`}
                >
                  {step.title}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* 배러셀 영역 */}
      <div className="desktop:px-0 desktop:py-16 relative px-4 pt-6 pb-[90px]">
        <Carousel
          setApi={setApi}
          opts={{
            align: 'start',
            loop: true,
            slidesToScroll: 1,
          }}
          plugins={[
            Autoplay({
              delay: 2000,
              stopOnMouseEnter: true,
              stopOnInteraction: false,
            }),
          ]}
          className="desktop:web-w desktop:mx-auto w-full"
        >
          <CarouselContent>
            {steps.map((step, index) => (
              <CarouselItem key={step.stepNumber} className="w-full">
                {renderItem ? (
                  renderItem(step, index)
                ) : (
                  <DefaultStepContent step={step} />
                )}
              </CarouselItem>
            ))}
          </CarouselContent>

          {/* 좌측 화살표 버튼 */}
          <button
            onClick={handlePrev}
            className="desktop:size-12 desktop:left-4 desktop:-ml-[42px] absolute top-1/2 -left-3 z-10 -translate-y-1/2 transform cursor-pointer disabled:cursor-not-allowed disabled:opacity-30"
            aria-label="이전 단계"
          >
            <div className="desktop:size-12 desktop:rounded-full flex size-8 items-center justify-center rounded-full bg-white shadow-lg transition hover:bg-gray-100">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="desktop:size-6 text-main-orange size-5"
              >
                <path
                  d="M15 18L9 12L15 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </button>

          {/* 우측 화살표 버튼 */}
          <button
            onClick={handleNext}
            className="desktop:size-12 desktop:right-4 desktop:-mr-[42px] absolute top-1/2 -right-3 z-10 -translate-y-1/2 transform cursor-pointer disabled:cursor-not-allowed disabled:opacity-30"
            aria-label="다음 단계"
          >
            <div className="desktop:size-12 desktop:rounded-full flex size-8 items-center justify-center rounded-full bg-white shadow-lg transition hover:bg-gray-100">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="desktop:size-6 text-main-orange size-5"
              >
                <path
                  d="M9 18L15 12L9 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </button>
        </Carousel>
      </div>
    </div>
  );
}

// 기본 Step 콘텐츠 렌더링 함수 (기존 구조 유지)
function DefaultStepContent({ step }: { step: CurriculumStepCarouselItem }) {
  const stepWithDefaults = step as CurriculumStepCarouselItem & {
    imageSrc?: string;
    imageAlt?: string;
    description?: string;
    operations?: string[];
  };

  return (
    <div className="desktop:flex desktop:gap-8 desktop:p-[48px] box-border flex flex-col gap-5 rounded-lg bg-white px-5 py-7">
      {/* 헤더 */}
      <div className="desktop:flex desktop:items-center desktop:gap-4 flex items-center gap-3">
        <span className="desktop:px-4 desktop:py-2 desktop:typo-18 desktop:rounded-full border-main-orange typo-14 bg-main-orange/5 text-main-orange rounded-full border px-3 py-2 font-extrabold">
          STEP {step.stepNumber}
        </span>
        <h3 className="desktop:typo-36 typo-24 text-level-2 font-black">
          {step.title}
        </h3>
      </div>

      {/* 메인 콘텐츠 영역 */}
      {stepWithDefaults.imageSrc && (
        <div className="desktop:grid desktop:grid-cols-2 desktop:gap-8 flex flex-col gap-6">
          <div className="desktop:gap-7 flex flex-col gap-4">
            {/* 이미지 영역 */}
            <div className="desktop:relative desktop:w-[518px] desktop:h-[220px] relative aspect-[287/122] w-full overflow-hidden rounded-lg">
              <ImageOptimizer
                src={stepWithDefaults.imageSrc}
                alt={stepWithDefaults.imageAlt || step.title}
                fill
                className="object-cover"
              />
            </div>
            {/* 설명 텍스트 */}
            {stepWithDefaults.description && (
              <p className="desktop:typo-22 typo-16 text-level-3 desktop:leading-snug leading-relaxed font-semibold">
                {stepWithDefaults.description}
              </p>
            )}
          </div>
          {/* 목록 영역 */}
          <div className="desktop:flex desktop:flex-col desktop:justify-between flex flex-col gap-6">
            {/* 조작 목록 */}
            {stepWithDefaults.operations &&
              stepWithDefaults.operations.length > 0 && (
                <div className="desktop:flex desktop:flex-col desktop:gap-9 desktop:pl-[40px] desktop:pt-[56px] relative flex h-full flex-col gap-6 rounded-2xl bg-[#F8F8F8] px-7 py-9">
                  {stepWithDefaults.operations.map((operation, opIndex) => (
                    <div
                      key={opIndex}
                      className="desktop:flex desktop:items-center desktop:gap-3 relative flex items-center gap-2"
                    >
                      <div className="desktop:size-5 desktop:flex-shrink-0 bg-main-orange relative z-10 flex size-4 flex-shrink-0 items-center justify-center rounded-full">
                        <div className="desktop:size-2 size-2 rounded-full bg-white" />
                      </div>
                      {opIndex < stepWithDefaults.operations!.length - 1 && (
                        <div
                          className="desktop:left-[10px] desktop:top-6 desktop:h-4 desktop:w-[1px] absolute top-5 left-[8px] h-3 w-[1px]"
                          style={{
                            borderLeft: '1px dashed #6D738B',
                            height: 'calc(100% + 0.75rem)',
                          }}
                        />
                      )}
                      <span className="desktop:typo-22 typo-14 text-level-3 font-extrabold">
                        {operation}
                      </span>
                    </div>
                  ))}
                </div>
              )}
          </div>
        </div>
      )}
    </div>
  );
}
